import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import {
  Link, NavLink, Outlet, useLocation, useNavigate, useOutletContext,
} from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { getStatus } from '../api';
import CommandBar from './CommandBar';
import { Mark, SearchIcon } from './icons';
import 'flag-icons/css/flag-icons.min.css';
import './Terminal.css';

/* The app shell: brand, search, coverage, footer. Everything else is a route.
 *
 * This replaced a two-pane layout where a fixed list of open trends sat beside
 * whatever you had last opened. The two never referred to each other - search
 * for a company and it appeared on the right while the left rail carried on
 * showing unrelated trends - so the pane read as decoration rather than
 * navigation. Pages own the full width now and the list is a page of its own. */

const MOD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  ? '\u2318'
  : 'Ctrl';

export const useWorkspace = () => useOutletContext();

/* Where the reader was on each page, so coming back from a trend puts them
 * where they left rather than at the top of the grid.
 *
 * Kept per path for the life of the tab. Not sessionStorage: the offset is only
 * meaningful against the list as it was, and a reload refetches a list that may
 * have changed length. */
const OFFSETS = new Map();

function useScrollMemory(ref) {
  const { pathname } = useLocation();

  // Layout, not effect: this runs between the new page being put in the DOM and
  // the browser painting it, so the reader never sees the frame where the new
  // page is sitting at the old page's offset.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const want = OFFSETS.get(pathname) || 0;
    let restoring = want > 0;

    // A page arriving for the first time starts at the top. The shell scrolls,
    // not the window, so without this the detail view opened already scrolled
    // down by however far the grid behind it had been.
    el.scrollTop = want;
    if (el.scrollTop >= want - 1) restoring = false;

    // Returning to the grid remounts it and it refetches, so at this point it
    // is skeletons and far too short to hold the offset. Rather than guess at a
    // delay - which on a cold database is anyone's guess - reapply it as the
    // real tiles arrive and stop as soon as it takes.
    const grow = restoring && el.firstElementChild
      ? new ResizeObserver(() => {
        if (!restoring) return;
        el.scrollTop = want;
        if (el.scrollTop >= want - 1) { restoring = false; grow.disconnect(); }
      })
      : null;
    grow?.observe(el.firstElementChild);

    // The moment the reader scrolls themselves, the restore is no longer
    // something being done for them, it is something fighting them.
    const release = () => { restoring = false; grow?.disconnect(); };
    const remember = () => { if (!restoring) OFFSETS.set(pathname, el.scrollTop); };

    // A list that came back shorter than it went never reaches the old offset,
    // and while it is still trying, nowhere the reader scrolls to is recorded.
    // Give up rather than hold the page's memory hostage to a week that closed
    // a few trends.
    const giveUp = setTimeout(release, 4000);

    el.addEventListener('wheel', release, { passive: true });
    el.addEventListener('touchstart', release, { passive: true });
    el.addEventListener('scroll', remember, { passive: true });

    return () => {
      clearTimeout(giveUp);
      release();
      el.removeEventListener('wheel', release);
      el.removeEventListener('touchstart', release);
      el.removeEventListener('scroll', remember);
    };
  }, [pathname, ref]);
}

export default function Workspace() {
  const [status, setStatus] = useState(null);
  const [cmdOpen, setCmdOpen] = useState(false);
  const navigate = useNavigate();
  const main = useRef(null);

  useScrollMemory(main);

  useEffect(() => {
    getStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  // Only the modifier chord. A bare "/" is a shortcut you have to be told
  // about, and it fires on anyone who types a slash while reading.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onPick = useCallback(
    (row) => {
      if (row.kind === 'term' && row.series_id) navigate(`/terms/${row.series_id}`);
      else navigate(`/companies/${row.company_id}`);
    },
    [navigate]
  );

  const cov = status?.coverage;
  const run = status?.run;

  return (
    <div className="tw">
      <header className="tw-bar">
        {/* The wordmark goes to the actual home page. Pointing it at /trends
          * meant there was no way back out to the site from inside the app,
          * and the Trends nav link one inch to the right already went there. */}
        <Link to="/" className="tw-brand" aria-label="Inflection home">
          <Mark />
          Inflection
        </Link>

        {/* Two things live here - what is moving, and everything we cover - and
          * until they were both named in the bar the second one had no door.
          * You could only reach a company by searching for it, which means
          * already knowing it exists. */}
        <nav className="tw-nav">
          <NavLink to="/trends" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Trends
          </NavLink>
          <NavLink to="/companies" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Companies
          </NavLink>
        </nav>

        <button type="button" className="tw-search" onClick={() => setCmdOpen(true)}>
          <SearchIcon />
          <span className="label">Search companies, brands and terms</span>
          <span className="tw-kbd">{MOD} K</span>
        </button>

        <span className="tw-bar-spacer" />

        {cov && (
          <div className="tw-stats">
            <span className="tw-stat">
              <b>{cov.companies.toLocaleString()}</b> companies
            </span>
            <span className="tw-stat">
              <b>{cov.series.toLocaleString()}</b> terms weekly
            </span>
            <span className="tw-stat">
              <i className="tw-live" />
              <b>{cov.open_episodes}</b> open
            </span>
          </div>
        )}

        {/* Signing out lived only on the marketing header, so from inside the
          * app the only way to it was to navigate off the app first. */}
        <div className="tw-user">
          <UserButton afterSignOutUrl="/" />
        </div>
      </header>

      <main className="tw-main" ref={main}>
        <Outlet context={{ status, cov, run, openSearch: () => setCmdOpen(true) }} />
      </main>

      <footer className="tw-foot">
        <span>Google Trends, scanned weekly</span>
        {run?.run_id && <span>Run {run.run_id}</span>}
        <span className="tw-bar-spacer" />
        <a href="mailto:xoxo@inflection.pro">xoxo@inflection.pro</a>
      </footer>

      <CommandBar open={cmdOpen} onClose={() => setCmdOpen(false)} onPick={onPick} />
    </div>
  );
}
